import AppointmentUSPBlock, { GaEventLabelProps } from '@components/AppointmentUSPBlock'
import CategoryButtons from '@components/CategoryButtons'
import CtaBanner from '@components/ContentBlocks/CtaBanner'
import InfoBlockSlider from '@components/InfoBlockSlider'
import OverviewHeader from '@components/OverviewHeader'
import { Footer, Header, Page, PageTransition } from '@components/Page'
import ProductGrid from '@components/_product-overview/ProductGrid'
import { SingleItemResponse } from '@lib/kontent/fetchContent'
import { BrandPage } from '@lib/kontent/models/BrandPage'
import { CategoryPage } from '@lib/kontent/models/CategoryPage'
import { ThemePage } from '@lib/kontent/models/ThemePage'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import * as qs from 'query-string'
import React from 'react'
import { pickSeoProps } from '@components/Page/Page'
import { useLocalization } from '@components/Localization'

const parseFilters = query => {
  let filters = {}
  Object.keys(query)
    .filter(key => !['sort', 'page'].includes(key))
    .forEach(key => {
      if (!isNaN(parseInt(key, 10))) {
        filters[parseInt(key, 10)] = query[key].split(',').map(i => parseInt(i, 10))
      }
    })

  return filters
}

const parseFixedFilters = (str: string) => {
  let obj = {}
  str.split('&').forEach(slice => {
    const parsed = qs.parse(slice)
    Object.keys(parsed).forEach(key => {
      obj[parseInt(key, 10)] = (parsed[key] as string).split(',').map(value => parseInt(value, 10))
    })
  })

  return obj
}

type Props = SingleItemResponse<CategoryPage | BrandPage | ThemePage> & {
  categoriesAndSubBrands?: any[]
}

const ProductOverview: NextPage<Props> = ({ item, modular_content, categoriesAndSubBrands }) => {
  const { messages, lang } = useLocalization()
  const router = useRouter()
  const scrollToAppointment = () => {
    const firstInput = document.getElementsByName('name')
    const contact = document.getElementById('make-an-appointment')
    const rect = contact.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop
    if (contact) {
      window.scroll({ top: rect.top + scrollTop, left: 0, behavior: 'smooth' })
      firstInput[0] &&
        firstInput[0].focus({
          preventScroll: true
        })
    }
  }
  const query = router.asPath.includes('?') ? qs.parse(router.asPath.split('?').pop()) : {}
  const initialPage = parseInt(query['page'] as string, 10) || 1
  const initialSort = (query['sort'] as string) || 'default'
  const fixedFilters = parseFixedFilters(item.elements.daf_filters__attributes.value)
  const initialFilters = query ? { ...parseFilters(query), ...fixedFilters } : {}

  const contentSlides = item.elements.content_slides.value.map(key => modular_content[key].elements)
  const callToActions = item.elements.product_grid_call_to_actions__items.modular_content.map(
    key => ({
      index: modular_content[key].elements.position.value,
      item: modular_content[modular_content[key].elements.item.value[0]].elements
    })
  )

  let breadcrumbs = [
    {
      href: '/[...slug]',
      as: `/${item.elements.url_slug}`,
      label: item.elements.title.value
    }
  ]
  // If category is watch subcategory, add "Watches" to breadcrumbs
  if (
    item.elements.daf_filters__article_type_id.value[0] === '1' &&
    item.system.codename !== 'horloges' &&
    item.system.type === 'category_page'
  ) {
    breadcrumbs.unshift({
      href: '/[...slug]',
      as: `/${messages.urlSlugItems.watches}`,
      label: lang === 'nl' ? 'Horloges' : 'Watches'
    })
  }

  let collections = []
  let fixedCollections = []
  if (item.elements.daf_filters__collection_nr.value.length) {
    fixedCollections = item.elements.daf_filters__collection_nr.value
      .split(',')
      .map(i => parseInt(i, 10))
  }
  if (query.collections) {
    collections = (query.collections as string).split(',').map(i => parseInt(i, 10))
  }

  let brands = []
  let fixedBrands = []
  if (item.elements.daf_filters__brand_nr.value.length) {
    fixedBrands = item.elements.daf_filters__brand_nr.value.split(',').map(i => parseInt(i, 10))
  }
  if (query.brands) {
    brands = (query.brands as string).split(',').map(i => parseInt(i, 10))
  }

  let gaEventLabel: GaEventLabelProps = lang === 'nl' ? 'CategoryPageNL' : 'CategoryPageEN'

  if (item.system.type === 'brand_page') {
    gaEventLabel = lang === 'nl' ? 'BrandPageNL' : 'BrandPageEN'
  }

  return (
    <Page {...pickSeoProps(item.elements)}>
      <Header initialVariant="absolute" />
      <PageTransition>
        {item.system.type === 'theme_page' ? (
          <CtaBanner
            mobileSrc={(item.elements as ThemePage).cta_banner__mobile_image?.value[0]?.url}
            desktopSrc={(item.elements as ThemePage).cta_banner__desktop_image?.value[0]?.url}
            buttonText={(item.elements as ThemePage).cta_banner__button_text?.value}
            buttonHref={(item.elements as ThemePage).cta_banner__button_url?.value}
            alt={(item.elements as ThemePage).cta_banner__desktop_image?.value[0]?.description}
          ></CtaBanner>
        ) : (
          <OverviewHeader
            title={(item.elements as CategoryPage).category_brand_header__title?.value}
            text={(item.elements as CategoryPage).category_brand_header__description?.value}
            image={(item.elements as CategoryPage).category_brand_header__image?.value[0]?.url}
            alt={
              (item.elements as CategoryPage).category_brand_header__image?.value[0]?.description
            }
            onClickMakeAppointment={() => scrollToAppointment()}
            breadcrumbItems={breadcrumbs}
          />
        )}

        {categoriesAndSubBrands && <CategoryButtons items={categoriesAndSubBrands} />}
        <ProductGrid
          type={item.system.type}
          initialPage={initialPage}
          initialSort={initialSort}
          initialFilters={initialFilters}
          fixedFilters={fixedFilters}
          theme={
            item.elements.daf_filters__theme.value.length
              ? item.elements.daf_filters__theme.value
              : undefined
          }
          callToActions={callToActions}
          fixedBrands={fixedBrands}
          brands={brands}
          fixedCollections={fixedCollections}
          collections={collections}
          articleTypeID={
            item.elements.daf_filters__article_type_id.value.length
              ? parseInt(item.elements.daf_filters__article_type_id.value, 10)
              : undefined
          }
        ></ProductGrid>
        <AppointmentUSPBlock
          mt="4rem"
          hasFullSizeBackground={!!contentSlides}
          subject={`Vraag vanaf ${item.elements.title.value} pagina`}
          gaEventLabel={gaEventLabel}
        />
        {contentSlides && <InfoBlockSlider items={contentSlides} />}
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default ProductOverview
